import React from 'react'
import { Modal, Radio, Button, Icon } from 'antd'

const FORMAT_OPTIONS = [
  {
    value: 'pdf',
    icon: 'file-pdf',
    title: 'PDF',
    description: 'Best for sharing, printing, and sign-off reviews',
    badge: 'Download only'
  },
  {
    value: 'document',
    icon: 'file-word',
    title: 'Document',
    description: 'Editable DOCX file for SOP workflows',
    badge: 'SOP supported'
  }
]

const QUALITY_OPTIONS = [
  {
    value: 'high',
    title: 'High quality',
    description: 'Best visuals, larger file'
  },
  {
    value: 'medium',
    title: 'Medium quality',
    description: 'Balanced output'
  },
  {
    value: 'low',
    title: 'Low quality',
    description: 'Fastest and smallest file'
  }
]

const sectionLabelStyle = {
  marginBottom: 10,
  color: '#111827',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.7,
  textTransform: 'uppercase'
}

const StoryboardDownloadModal = ({
  visible,
  preparing,
  progress,
  statusText,
  quality,
  format,
  generatedFileName,
  isGenerated,
  addingToSop,
  sopAdded,
  canStart,
  onQualityChange,
  onFormatChange,
  onDownload,
  onAddToSop,
  onCancel,
  onStart
}) => {
  const selectedFormat = format || 'pdf'
  const isDocumentFormat = selectedFormat === 'document'
  const isPrimaryBusy = preparing || addingToSop
  const formatLabel = isDocumentFormat ? 'Document' : 'PDF'

  const handleFormatKeyDown = (event, value) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onFormatChange(value)
    }
  }

  return (
    <Modal
      centered
      width={600}
      maskClosable={!isPrimaryBusy}
      closable={!isPrimaryBusy}
      visible={visible}
      title="Export Storyboard"
      onCancel={onCancel}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            flexWrap: 'wrap'
          }}
        >
          {!isPrimaryBusy && (
            <Button
              onClick={onCancel}
              style={{
                height: 44,
                width: 104,
                borderRadius: 6,
                fontWeight: 700
              }}
            >
              Cancel
            </Button>
          )}
          {!isGenerated ? (
            <Button
              type="primary"
              loading={preparing}
              disabled={isPrimaryBusy || !canStart}
              onClick={onStart}
              style={{
                height: 44,
                minWidth: 210,
                borderRadius: 6,
                borderColor: '#243bdb',
                background: '#243bdb',
                fontWeight: 700,
                boxShadow: '0 8px 18px rgba(36, 59, 219, 0.22)'
              }}
            >
              Prepare {formatLabel}
            </Button>
          ) : (
            <>
              <Button
                icon="download"
                onClick={onDownload}
                disabled={isPrimaryBusy}
                style={{
                  height: 44,
                  width: isDocumentFormat ? 176 : 220,
                  borderRadius: 6,
                  fontWeight: 700,
                  whiteSpace: 'nowrap'
                }}
              >
                Download
              </Button>
              {isDocumentFormat && (
                <Button
                  type={sopAdded ? "green" : "primary"}
                  icon={sopAdded ? 'check-circle' : 'folder-add'}
                  loading={addingToSop}
                  onClick={sopAdded ? null : onAddToSop}
                  style={{
                    height: 44,
                    width: 176,
                    borderRadius: 6,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {sopAdded ? 'Added to SOP' : 'Add to SOP'}
                </Button>
              )}
            </>
          )}
        </div>
      }
      bodyStyle={{ padding: '22px 24px 24px' }}
    >
      {!preparing ? (
        <div style={{ color: '#374151', lineHeight: 1.55 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 18,
              marginBottom: 22
            }}
          >
            <div>
              <div
                style={{
                  marginBottom: 6,
                  color: '#111827',
                  fontSize: 17,
                  fontWeight: 800
                }}
              >
                Prepare Storyboard Export
              </div>
              <div
                style={{
                  maxWidth: 430,
                  color: '#4b5563',
                  fontSize: 13.5
                }}
              >
                Choose the output your team needs. Documents unlock SOP upload;
                PDFs stay download-only.
              </div>
            </div>
            <div
              style={{
                flex: '0 0 auto',
                padding: '5px 9px',
                border: '1px solid #d1d5db',
                borderRadius: 999,
                background: '#ffffff',
                color: '#111827',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: 'uppercase'
              }}
            >
              {formatLabel}
            </div>
          </div>

          <div style={sectionLabelStyle}>Export Format</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
              marginBottom: 18,
              width: '100%'
            }}
          >
            {FORMAT_OPTIONS.map(option => {
              const isSelected = selectedFormat === option.value
              return (
                <div
                  key={option.value}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={() => onFormatChange(option.value)}
                  onKeyDown={event => handleFormatKeyDown(event, option.value)}
                  style={{
                    position: 'relative',
                    minHeight: 116,
                    padding: 16,
                    border: `1.5px solid ${isSelected ? '#243bdb' : '#d6dbe6'}`,
                    borderRadius: 8,
                    background: '#ffffff',
                    boxShadow: isSelected
                      ? '0 12px 28px rgba(36, 59, 219, 0.16)'
                      : '0 1px 0 rgba(17, 24, 39, 0.02)',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      width: 4,
                      borderTopLeftRadius: 8,
                      borderBottomLeftRadius: 8,
                      background: isSelected ? '#243bdb' : 'transparent'
                    }}
                  />
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginBottom: 14
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: isSelected ? '#eef2ff' : '#f3f4f6',
                          color: isSelected ? '#243bdb' : '#374151'
                        }}
                      >
                        <Icon type={option.icon} style={{ fontSize: 18 }} />
                      </span>
                      <strong
                        style={{
                          color: '#111827',
                          fontSize: 15,
                          fontWeight: 800
                        }}
                      >
                        {option.title}
                      </strong>
                    </span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 18,
                        height: 18,
                        border: `2px solid ${isSelected ? '#243bdb' : '#9ca3af'
                          }`,
                        borderRadius: '50%'
                      }}
                    >
                      {isSelected && (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#243bdb'
                          }}
                        />
                      )}
                    </span>
                  </span>
                  <span
                    style={{
                      display: 'block',
                      marginBottom: 12,
                      color: '#374151',
                      fontSize: 13
                    }}
                  >
                    {option.description}
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      minHeight: 22,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: isSelected ? '#111827' : '#eef0f4',
                      color: isSelected ? '#ffffff' : '#374151',
                      fontSize: 11,
                      fontWeight: 800
                    }}
                  >
                    {option.badge}
                  </span>
                </div>
              )
            })}
          </div>

          {isDocumentFormat ? (
            <div
              style={{
                marginBottom: 16,
                padding: '12px 14px',
                border: '1px solid #b8c7f5',
                borderRadius: 8,
                background: '#f8faff',
                color: '#1f2937',
                fontSize: 13
              }}
            >
              <Icon
                type="check-circle"
                style={{ marginRight: 8, color: '#1f7a4d' }}
              />
              Document exports create an editable DOCX file and are eligible for
              SOP Documents.
            </div>
          ) : (
            <>
              <div style={sectionLabelStyle}>Export Quality</div>
              <Radio.Group
                value={quality}
                onChange={e => onQualityChange(e.target.value)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 8,
                  marginBottom: 16,
                  padding: 12,
                  border: '1px solid #d6dbe6',
                  borderRadius: 8,
                  background: '#ffffff'
                }}
              >
                {QUALITY_OPTIONS.map(option => {
                  const isSelected = quality === option.value
                  return (
                    <Radio
                      key={option.value}
                      value={option.value}
                      style={{
                        margin: 0,
                        padding: '8px 10px',
                        borderRadius: 6,
                        background: isSelected ? '#f3f6ff' : 'transparent',
                        color: '#111827',
                        fontSize: 13
                      }}
                    >
                      <strong style={{ color: '#111827' }}>
                        {option.title}
                      </strong>
                      <span style={{ color: '#4b5563' }}>
                        {' '}
                        - {option.description}
                      </span>
                    </Radio>
                  )
                })}
              </Radio.Group>
            </>
          )}

          {!isDocumentFormat && (
            <div
              style={{
                padding: '12px 14px',
                border: '1px solid #d7a332',
                borderRadius: 8,
                background: '#fff9e8',
                color: '#6b4300',
                fontSize: 13,
                fontWeight: 600
              }}
            >
              <Icon type="info-circle" style={{ marginRight: 8 }} />
              SOP upload is available for document exports only.
            </div>
          )}

          {isGenerated && (
            <div
              style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: '1px solid #d6dbe6'
              }}
            >
              <div
                style={{ marginBottom: 10, color: '#374151', fontSize: 12.5 }}
              >
                Ready:{' '}
                <strong style={{ color: '#111827' }}>
                  {generatedFileName}
                </strong>
              </div>
              {sopAdded && (
                <div
                  style={{ marginBottom: 6, color: '#1f7a4d', fontSize: 12 }}
                >
                  Added to SOP Documents
                </div>
              )}
              <div style={{ color: '#4b5563', fontSize: 12.5 }}>
                {isDocumentFormat
                  ? 'Choose whether to download only, or download and add this document to SOP Documents.'
                  : 'PDF exports are ready to download.'}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div
            style={{
              marginBottom: 12,
              color: '#374151',
              fontSize: 13,
              fontWeight: 700
            }}
          >
            {statusText || `Preparing ${formatLabel.toLowerCase()}...`}
          </div>
          <div
            style={{
              width: '100%',
              height: 10,
              borderRadius: 999,
              background: '#e5e7eb',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: '#243bdb',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              textAlign: 'right',
              color: '#4b5563',
              fontSize: 12,
              fontWeight: 700
            }}
          >
            {progress}%
          </div>
        </div>
      )}
    </Modal>
  )
}

export default StoryboardDownloadModal
